import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../constants/language';
import { eventService } from '../../../services/event-service';
import { Event} from '../../../models/event/event';
import { Asset } from '../../../models/assets/asset';
import { LoadingSpinner, LocationLink, VideoPlayer } from '../../ui';
import { DateUtil } from '../../../utils/date-util';
import { EnrollmentMode } from '../../../models/enrollment/enrollment';
import UniversalEnrollButton from '../../ui/UniversalEnrollButton';

const EventDetail: React.FC = () => {
  const { eventKey, eventId } = useParams<{ eventKey: string; eventId: string }>();
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPdf, setSelectedPdf] = useState<Asset | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
  const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
  const [thumbnailLoadingStates, setThumbnailLoadingStates] = useState<Record<number, boolean>>({});
  const [thumbnailErrorStates, setThumbnailErrorStates] = useState<Record<number, boolean>>({});
  const [pdfLoadingStates, setPdfLoadingStates] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const fetchEvent = async () => {
      setIsLoading(true);
      try {
        let foundEvent: Event | undefined;

        if (eventId) {
          foundEvent = await eventService.getEventById(parseInt(eventId), i18n.language as Language);
        } else if (eventKey) {
          foundEvent = await eventService.getEventByKey(eventKey, i18n.language as Language);
        }

        setEvent(foundEvent || null);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvent();
  }, [eventKey, eventId, i18n.language]);

  // Initialize loading states when event loads
  useEffect(() => {
    if (event) {
      // Initialize image loading states
      if (event.media.photos) {
        const imageStates: Record<number, boolean> = {};
        event.media.photos.forEach((_, index) => {
          imageStates[index] = true;
        });
        setImageLoadingStates(imageStates);
      }

      // Initialize thumbnail loading states
      if (event.media.videos) {
        const thumbnailStates: Record<number, boolean> = {};
        event.media.videos.forEach((video, index) => {
          if (video.thumbnailUrl) {
            thumbnailStates[index] = true;
          }
        });
        setThumbnailLoadingStates(thumbnailStates);
      }
    }
  }, [event]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('pages.events.details.notFound')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('pages.events.noEventsMessage')}
          </p>
          <Link
            to="/events"
            className="bg-black text-white hover:bg-gray-800 px-6 py-3 rounded-lg font-medium transition-colors duration-300"
          >
            {t('pages.events.details.backToEvents')}
          </Link>
        </div>
      </div>
    );
  }

  const canEnroll = eventService.canEnroll(event);
  const isRTL = i18n.language === 'ar';

  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc);
  };

  const handleVideoChange = (index: number) => {
    setActiveVideoIndex(index);
    setVideoLoading(true);
    setVideoError(false);

    // Initialize thumbnail loading states if not already set
    setThumbnailLoadingStates(prev => {
      const newStates = { ...prev };
      if (!(index in newStates)) {
        newStates[index] = true;
      }
      return newStates;
    });
  };

  const isValidVideoUrl = (url?: string): boolean => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleImageLoad = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
    setImageErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleImageStart = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: true }));
    setImageErrorStates(prev => ({ ...prev, [index]: false }));
  };

  const handleThumbnailLoad = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleThumbnailError = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleThumbnailStart = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: true }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: false }));
  };

  const handlePdfClick = (pdf: Asset, index: number) => {
    setPdfLoadingStates(prev => ({ ...prev, [index]: true }));
    setSelectedPdf(pdf);
    // Simulate loading time for PDF viewer
    setTimeout(() => {
      setPdfLoadingStates(prev => ({ ...prev, [index]: false }));
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16 lg:py-24">
        <div className="absolute inset-0 opacity-10">
          <img
            src={event.media.cover?.url || event.media.logo.url}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-4 text-sm mb-8">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">
              {t('nav.home')}
            </Link>
            <span className="text-gray-500">/</span>
            <Link to="/events" className="text-gray-300 hover:text-white transition-colors">
              {t('nav.events')}
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-white">{event.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="bg-white/10 text-white px-3 py-1 text-xs font-medium rounded-full">
                    {t(`pages.events.categories.${event.category}`)}
                  </span>
                  {event.featured && (
                    <span className="bg-yellow-500 text-black px-3 py-1 text-xs font-medium rounded-full">
                      {t('pages.events.card.featured')}
                    </span>
                  )}
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                {event.title}
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                {event.description}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{DateUtil.formatDateRange(event.startDate, event.endDate)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{DateUtil.formatTimeRange(event.startTime, event.endTime)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  <LocationLink address={event.address}>
                    {event.address.name}
                  </LocationLink>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {/* Handle Ministry organizer object */}
                  {event.organizer ? (
                    <Link 
                      to={`/ministries/${event.organizer.id}`}
                      className="text-gray-300 hover:text-white transition-colors underline"
                    >
                      {event.organizer.title}
                    </Link>
                  ) : (
                    <span>Unknown Organizer</span>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                {event.enrollment.type !== EnrollmentMode.NONE && (
                  <UniversalEnrollButton
                    enrollmentUrl={event.enrollment.formUrl}
                    variant="secondary"
                    size="lg"
                    className="w-full py-3 px-6 text-lg font-semibold mb-4"
                    disabled={!canEnroll}
                  >
                    {event.enrollment.type === EnrollmentMode.MANDATORY
                      ? t('enrollment.enrollNow')
                      : t('enrollment.enrollOptional')
                    }
                  </UniversalEnrollButton>
                )}

                {/* Event Info */}
                <div className="space-y-4 text-sm">
                  {event.enrollment.cost && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{t('enrollment.cost')}:</span>
                      <span className="text-white font-medium">
                        {event.enrollment.cost.amount > 0
                          ? `${event.enrollment.cost.amount} ${event.enrollment.cost.currency}`
                          : t('enrollment.free')
                        }
                      </span>
                    </div>
                  )}

                  {event.enrollment.capacity && event.enrollment.enrolled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-300">{t('enrollment.enrolled')}:</span>
                        <span className="text-white font-medium">
                          {event.enrollment.enrolled}/{event.enrollment.capacity}
                        </span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-white h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min((event.enrollment.enrolled / event.enrollment.capacity) * 100, 100)}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {event.contactInfo && (
                    <div className="pt-4 border-t border-white/20">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <a href={`mailto:${event.contactInfo.EMAIL}`} className="text-gray-300 hover:text-white">
                          {event.contactInfo.EMAIL}
                        </a>
                      </div>
                      {event.contactInfo.PHONE && (
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="text-gray-300">{event.contactInfo.PHONE}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="text-3xl font-bold text-black mb-6">
                  {t('pages.events.details.description')}
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-600 leading-relaxed">
                    {event.longDescription}
                  </p>
                </div>
              </div>


              {/* Video Section */}
              {event.media.videos && event.media.videos.length > 0 && isValidVideoUrl(event.media.videos[activeVideoIndex]?.url) && (
                <section>
                  <h2 className="text-3xl font-bold mb-6">{t('general.video.title')}</h2>
                  <div className="bg-black rounded-2xl overflow-hidden mb-4 relative">
                    <div className="aspect-video">
                      {videoLoading && (
                        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center text-white z-10">
                          <div className="flex flex-col items-center space-y-4">
                            <svg className="w-12 h-12 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div className="text-sm">{t('ui.loading', 'Loading video...')}</div>
                          </div>
                        </div>
                      )}

                      {videoError ? (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center text-white">
                          <div className="text-center">
                            <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                              <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="text-xl mb-2">Error loading video</div>
                            <div className="text-sm text-gray-400 mb-4 max-w-md truncate">
                              {event.media.videos[activeVideoIndex]?.url}
                            </div>
                            <div className="space-y-2">
                              <button
                                onClick={() => {
                                  setVideoError(false);
                                  setVideoLoading(true);
                                }}
                                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors mr-2"
                              >
                                Retry
                              </button>
                              <a
                                href={event.media.videos[activeVideoIndex]?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 px-4 py-2 rounded-lg transition-colors inline-block"
                              >
                                Open in New Tab
                              </a>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <VideoPlayer
                          key={event.media.videos[activeVideoIndex]?.url}
                          url={event.media.videos[activeVideoIndex]?.url || ''}
                          className="w-full h-full"
                          poster={event.media.videos[activeVideoIndex]?.thumbnailUrl}
                          onLoadStart={() => {
                            setVideoLoading(true);
                            setVideoError(false);
                          }}
                          onCanPlay={() => {
                            setVideoLoading(false);
                          }}
                          onError={() => {
                            setVideoLoading(false);
                            setVideoError(true);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Video Gallery Thumbnails */}
                  {event.media.videos.length > 1 && (
                    <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3 overflow-x-auto pb-2 mb-4`}>
                      {event.media.videos.map((video: Asset, index: number) => (
                        <button
                          key={index}
                          onClick={() => handleVideoChange(index)}
                          className={`flex-shrink-0 relative w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            activeVideoIndex === index 
                              ? 'border-black shadow-lg' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {/* Video Thumbnail */}
                          {video.thumbnailUrl ? (
                            <div className="relative w-full h-full">
                              {/* Loading placeholder for thumbnail */}
                              {thumbnailLoadingStates[index] && (
                                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                  <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              )}

                              {/* Error placeholder for thumbnail */}
                              {thumbnailErrorStates[index] && (
                                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}

                              <img
                                src={video.thumbnailUrl}
                                alt={`Video ${index + 1} thumbnail`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  thumbnailLoadingStates[index] ? 'opacity-0' : 'opacity-100'
                                }`}
                                onLoadStart={() => handleThumbnailStart(index)}
                                onLoad={() => handleThumbnailLoad(index)}
                                onError={() => handleThumbnailError(index)}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}

                          {/* Play Icon Overlay */}
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div className={`w-6 h-6 flex items-center justify-center rounded-full ${
                              activeVideoIndex === index ? 'bg-white text-black' : 'bg-white/80 text-black'
                            }`}>
                              <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>

                          {/* Active Border */}
                          {activeVideoIndex === index && (
                            <div className="absolute inset-0 border-2 border-black rounded-lg pointer-events-none"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Video Counter */}
                  {event.media.videos.length > 1 && (
                    <div className="text-center mb-4">
                      <p className="text-sm text-gray-600">
                        Video {activeVideoIndex + 1} of {event.media.videos.length}
                      </p>
                    </div>
                  )}
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-8">
                {/* Photo Gallery Carousel */}
                {event.media.photos && event.media.photos.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-bold mb-4">📸 {t('general.gallery.title')}</h3>
                    <div className="relative">
                      {/* Main carousel container */}
                      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <button
                          onClick={() => handleImageClick(event.media.photos[currentImageIndex]?.url || '')}
                          className="w-full h-full flex items-center justify-center hover:opacity-90 transition-opacity group relative overflow-hidden"
                        >
                          {event.media.photos[currentImageIndex]?.url ? (
                            <div className="relative w-full h-full">
                              {/* Loading placeholder - show while image is loading */}
                              {imageLoadingStates[currentImageIndex] && (
                                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                  <svg className="w-16 h-16 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </div>
                              )}

                              {/* Error placeholder - show if image failed to load */}
                              {imageErrorStates[currentImageIndex] && (
                                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                  <div className="text-center">
                                    <svg className="w-16 h-16 text-gray-400 mb-2 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm text-gray-500">Failed to load</p>
                                  </div>
                                </div>
                              )}

                              <img
                                src={event.media.photos[currentImageIndex].url}
                                alt={event.media.photos[currentImageIndex].name || `Photo ${currentImageIndex + 1}`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  imageLoadingStates[currentImageIndex] ? 'opacity-0' : 'opacity-100'
                                }`}
                                onLoadStart={() => handleImageStart(currentImageIndex)}
                                onLoad={() => handleImageLoad(currentImageIndex)}
                                onError={() => handleImageError(currentImageIndex)}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <svg className="w-16 h-16 text-gray-400 group-hover:text-gray-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          {/* Click to expand overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-black px-4 py-2 rounded-lg font-medium">
                              {t('general.gallery.click-to-enlarge')}
                            </div>
                          </div>
                        </button>

                        {/* Navigation arrows */}
                        {event.media.photos.length > 1 && (
                          <>
                            <button
                              onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : event.media.photos.length - 1)}
                              className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                            >
                              <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setCurrentImageIndex(currentImageIndex < event.media.photos.length - 1 ? currentImageIndex + 1 : 0)}
                              className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                            >
                              <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>

                      {/* Thumbnail navigation */}
                      {event.media.photos.length > 1 && (
                        <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-2 mt-4 overflow-x-auto pb-2`}>
                          {event.media.photos.map((photo: Asset, index: number) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                index === currentImageIndex 
                                  ? 'border-black shadow-md' 
                                  : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              {photo.url ? (
                                <div className="relative w-full h-full">
                                  {/* Loading placeholder for thumbnail */}
                                  {imageLoadingStates[index] && (
                                    <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}

                                  {/* Error placeholder for thumbnail */}
                                  {imageErrorStates[index] && (
                                    <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </div>
                                  )}

                                  <img
                                    src={photo.url}
                                    alt={photo.name || `Photo ${index + 1}`}
                                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                                      imageLoadingStates[index] ? 'opacity-0' : 'opacity-100'
                                    }`}
                                    onLoadStart={() => handleImageStart(index)}
                                    onLoad={() => handleImageLoad(index)}
                                    onError={() => handleImageError(index)}
                                  />
                                </div>
                              ) : (
                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Image counter */}
                      {event.media.photos.length > 1 && (
                        <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {currentImageIndex + 1} / {event.media.photos.length}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Spacing */}
                {event.media.photos && event.media.photos.length > 0 && <div className="mb-6"></div>}

                {/* PDF Preview Section */}
                {event.media.files && event.media.files.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16v-1a4 4 0 014-4h4m-6 5v2a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                      </svg>
                      <span>{t('general.pdf.title')}</span>
                    </h3>
                    <div className="space-y-3">
                      {event.media.files.map((pdf: Asset, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => handlePdfClick(pdf, idx)}
                          disabled={pdfLoadingStates[idx]}
                          className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            {pdfLoadingStates[idx] ? (
                              <>
                                <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="font-medium text-gray-500 truncate animate-pulse">
                                  {t('ui.loading', 'Loading...')}
                                </span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="font-medium text-gray-800 truncate">
                                  {pdf.name}
                                </span>
                              </>
                            )}
                          </div>
                          <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Spacing */}
                {event.media.files && event.media.files.length > 0 && <div className="mb-6"></div>}

                {/* Event Info Card */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-black mb-4">
                    {t('pages.events.details.location')}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <div className="text-gray-500 mb-1">{t('general.location')}</div>
                      <div className="font-medium">
                        <LocationLink address={event.address}>
                          {event.address.name}
                        </LocationLink>
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">{t('pages.events.card.startTime')}</div>
                      <div className="font-medium">{DateUtil.formatTimeRange(event.startTime, event.endTime)} at {DateUtil.formatTimeRange(event.startTime, event.endTime)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">{t('pages.events.card.organizer')}</div>
                      <div className="font-medium">
                        {/* Handle Ministry organizer object */}
                        {event.organizer ? (
                          <Link 
                            to={`/ministries/${event.organizer.id}`}
                            className="text-black hover:text-gray-600 transition-colors underline"
                          >
                            {event.organizer.title}
                          </Link>
                        ) : (
                          'Unknown Organizer'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PDF Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16v-1a4 4 0 014-4h4m-6 5v2a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                </svg>
                <span className="font-bold text-lg">PDF Preview</span>
              </div>
              <button
                onClick={() => setSelectedPdf(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* PDF Viewer Container */}
            <div className="flex-1 p-4">
              <div className="w-full h-full bg-gray-100 rounded border">
                <iframe
                  src={selectedPdf?.url || ''}
                  title="PDF Preview"
                  width="100%"
                  height="100%"
                  className="rounded"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className={`p-4 border-t border-gray-200 flex justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
              <a
                href={selectedPdf?.url || ''}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Open in New Tab
              </a>
              <a
                href={selectedPdf?.url || ''}
                download
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-full p-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Gallery View</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={selectedImage || ''}
                  alt="Enlarged gallery view"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;